/* globals Stripe */
import './payment.scss';
import merge from 'lodash/merge';
import Spinner from '../../components/spinner/spinner';
import Dialog from '../dialog/dialog';

/**
 * Legacy component
 */
function Payment(element) {
	this.ui = {
		element: element
	};

	this.settings = merge(
		{
			key: 'pk_test_XX4aZ12JWyK9wEK06KoFMN44',
			locale: 'de',
			currency: 'chf',
			panelLabel: 'Bezahle',
			panelLabelFree: 'Kreditkarte hinterlegen',
			stripeError:
				'Es gab einen Fehler bei unserem Zahlungsanbieter. Bitte lade die Seite neu und versuche es noch einmal.',
			initialTotal: 0
		},
		JSON.parse(this.ui.element.dataset.paymentOptions || '{}')
	);

	this.freeLifetime = false;

	this.data = {
		paymentAmount: 0,
		paymentMethodId: null
	};

	this.api = null;

	this.init();
}

Payment.prototype.init = function () {
	this.ui.button = this.ui.element.querySelector('[data-payment="button"]');
	this.ui.amount = this.ui.element.querySelector('[data-payment="amount"]');
	this.ui.coupon = this.ui.element.querySelector('[data-payment="coupon"]');
	this.ui.couponInput = this.ui.element.querySelector('[data-payment="couponInput"]');
	this.ui.couponSubmit = this.ui.element.querySelector('[data-payment="couponSubmit"]');
	this.ui.couponFeedback = this.ui.element.querySelector('[data-payment="couponFeedback"]');
	this.ui.discount = this.ui.element.querySelector('[data-payment="discount"]');
	this.ui.total = this.ui.element.querySelector('[data-payment="total"]');
	this.ui.totalInfo = this.ui.element.querySelector('[data-payment="totalInfo"]');
	this.ui.paymentInfo = this.ui.element.querySelector('[data-payment="paymentInfo"]');
	this.ui.paymentInfoCoupon = this.ui.element.querySelector('[data-payment="paymentInfoCoupon"]');
	this.ui.error = this.ui.element.querySelector('[data-payment="error"]');
	this.ui.dialog = this.ui.element.querySelector('[data-payment="dialog"]');
	this.ui.dialogPaymentElement = this.ui.element.querySelector(
		'[data-payment="dialogPaymentElement"]'
	);
	this.ui.dialogError = this.ui.element.querySelector('[data-payment="dialogError"]');
	this.ui.dialogButton = this.ui.element.querySelector('[data-payment="dialogButton"]');
	this.ui.dialogButtonText = this.ui.element.querySelector('[data-payment="dialogButtonText"]');
	this.ui.variants = this.ui.element.querySelectorAll('[data-payment-amount]');
	this.ui.csrf = this.ui.element.querySelector('[name=_csrf]');

	this.dialog = new Dialog();

	this.submitSpinner = new Spinner(this.ui.button);
	this.dialogSubmitSpinner = new Spinner(this.ui.dialogButton);

	this._updatePaymentAmount(this.settings.initialTotal);

	this.ui.element.addEventListener('submit', async event => {
		event.preventDefault();

		this.submitSpinner.enable();

		this._resetError();

		if (!this.freeLifetime && !this.data.paymentMethodId) {
			// Gather missing payment data unless we are signing up to a free lifetime account
			this.api = this.api || Stripe(this.settings.key);

			if (!this.api) {
				this._showError(new Error(this.settings.stripeError));

				return;
			}

			const selectedPlan = this._getSelectedPlan();
			const isSubscription = selectedPlan && selectedPlan.value.match(/plan__/);

			this.ui.dialogButtonText.innerText = this.data.paymentAmount
				? this.settings.panelLabel
				: this.settings.panelLabelFree;

			this.dialog.show(this.ui.dialog, {
				modal: true,
				onClose: () => {
					this.submitSpinner.disable();
				}
			});

			const elementsOptions = {
				mode: selectedPlan ? (isSubscription ? 'subscription' : 'payment') : 'setup',
				currency: this.settings.currency,
				paymentMethodCreation: 'manual',
				locale: this.settings.locale
			};

			if (selectedPlan) {
				elementsOptions.amount = this.data.paymentAmount;
			}

			this.apiElements = this.api.elements(elementsOptions);

			const paymentElementOptions = {};

			this.apiPaymentElement = this.apiElements.create('payment', paymentElementOptions);

			this.apiPaymentElement.mount(this.ui.dialogPaymentElement);

			return;
		}

		// Submit form data
		const formData = new FormData(this.ui.element);

		for (const [key, value] of Object.entries(this.data)) {
			formData.append(key, value);
		}

		const { error, status, clientSecret, redirect } = await fetch(this.ui.element.action, {
			method: 'POST',
			body: JSON.stringify(Object.fromEntries(formData.entries())),
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'XMLHttpRequest'
			}
		}).then(response => response.json());

		if (error) {
			this._showError(new Error(error));
		}

		if (['requires_action', 'requires_source_action'].includes(status)) {
			const { error } = await this.api.handleNextAction({
				clientSecret
			});

			this._showError(new Error(error));
		}

		if (redirect) {
			window.location.href = redirect;
		}
	});

	this.ui.dialogButton.addEventListener('click', async () => {
		this.dialogSubmitSpinner.enable();

		this._resetError();

		const { error: submitError } = await this.apiElements.submit();

		if (submitError) {
			this._showError(submitError);

			return;
		}

		const { error, paymentMethod } = await this.api.createPaymentMethod({
			elements: this.apiElements
		});

		if (error) {
			this._showError(error);

			return;
		}

		this.data.paymentMethodId = paymentMethod.id;

		this.ui.element.dispatchEvent(new Event('submit', { cancelable: true }));
	});

	[].slice.call(this.ui.variants).forEach(variant => {
		variant.addEventListener('change', () => {
			this._updateSummary(variant);
		});
	});

	if (this.ui.couponInput) {
		this.ui.couponInput.addEventListener('keydown', event => {
			if (event.which === 13) {
				event.preventDefault();

				this._submitCoupon();
			}
		});

		if (this.ui.couponInput.value) {
			this._submitCoupon();
		}
	}

	if (this.ui.couponSubmit) {
		this.couponSpinner = new Spinner(this.ui.couponSubmit);

		this.ui.couponSubmit.addEventListener('click', () => {
			this._submitCoupon();
		});
	}

	// Handle CSRF error
	if (this.ui.couponFeedback) {
		this.ui.couponFeedback.addEventListener('click', event => {
			if (event.target.matches('[data-init="reload"]')) {
				event.preventDefault();

				// Not using reload since we need to avoid repeating POST requests
				window.location = window.location.href;
			}
		});
	}
};

Payment.prototype._updateSummary = function (variant) {
	var amount = Number(variant.getAttribute('data-payment-amount'));

	this.ui.amount.textContent = amount;
	this.ui.discount.textContent = 0;
	this.ui.coupon.value = '';
	this.ui.total.textContent = amount;

	this.ui.totalInfo.style.display = 'none';

	this._updatePaymentAmount(amount);

	if (this.ui.couponInput.value) {
		this._submitCoupon();
	}
};

Payment.prototype._getSelectedPlan = function () {
	return [].slice.call(this.ui.variants).find(plan => plan.checked);
};

Payment.prototype._submitCoupon = function () {
	var selectedPlan = this._getSelectedPlan(),
		isSubscription = selectedPlan && selectedPlan.value.match(/plan__/),
		payload = {
			coupon: this.ui.couponInput.value,
			amount: selectedPlan ? selectedPlan.getAttribute('data-payment-amount') : null,
			plan: selectedPlan ? selectedPlan.value : null,
			_csrf: this.ui.csrf.value
		};

	this.ui.couponFeedback.innerHTML = '';
	this.ui.totalInfo.style.display = 'none';
	this.ui.paymentInfo.style.display = 'block';
	this.ui.paymentInfoCoupon.style.display = 'none';

	if (this.couponSpinner) {
		this.couponSpinner.enable();
	}

	this.freeLifetime = false;

	fetch(this.settings.couponAction, {
		method: 'POST',
		credentials: 'same-origin',
		body: JSON.stringify(payload),
		headers: {
			'Content-Type': 'application/json',
			'X-Requested-With': 'XMLHttpRequest'
		}
	})
		.then(response => response.json())
		.then(data => {
			if (this.couponSpinner) {
				this.couponSpinner.disable();
			}

			this.ui.discount.textContent = data.discount;
			this.ui.coupon.value = data.coupon || '';
			this.ui.total.textContent = data.total;

			this._updatePaymentAmount(data.total);

			if (isSubscription) {
				if (data.duration !== 'forever') {
					this.ui.totalInfo.style.display = 'inline';
				}

				if (data.total === 0) {
					this.ui.paymentInfoCoupon.style.display = 'block';
				}
			} else if (data.total === 0) {
				this.ui.paymentInfo.style.display = 'none';

				this.freeLifetime = true;
			}

			if (data.message) {
				this.ui.couponFeedback.innerHTML = data.message;
			}

			if (data.error) {
				throw data.error;
			}
		})
		.catch(err => {
			if (this.couponSpinner) {
				this.couponSpinner.disable();
			}

			this.ui.couponFeedback.innerHTML = `<div class="ln-form__error">${err}</div>`;
		});
};

Payment.prototype._showError = function (error) {
	if (this.dialog.dialog.shown) {
		this.ui.dialogError.innerHTML = error.message;

		this.dialogSubmitSpinner.disable();
	} else {
		this.ui.error.innerHTML = error.message;

		this.submitSpinner.disable();
	}

	console.error(error.message);
};

Payment.prototype._resetError = function () {
	this.ui.error.innerHTML = '';
	this.ui.dialogError.innerHTML = '';
};

Payment.prototype._updatePaymentAmount = function (input) {
	this.data.paymentAmount = Number(input) * 100;
};

document.querySelectorAll('[data-init~="payment"]').forEach(element => {
	new Payment(element);
});
